import { useEffect, useRef, useState } from 'react';

import { countScopeValues, defaultRangeStartISO, useFilterStore } from './filterStore';
import { useAccount } from '../../contexts/AccountContext';
import { scopeKeyLabel, type ExtranetPermission, type ScopeKey } from '../../domain/access';
import { MultiSelect } from '../common/MultiSelect';
import { IconChevronDown } from '../incidents/icons';

const MONTHS = [
  'janvier',
  'février',
  'mars',
  'avril',
  'mai',
  'juin',
  'juillet',
  'août',
  'septembre',
  'octobre',
  'novembre',
  'décembre',
];

const toInputValue = (iso: string) => iso.slice(0, 10);
const fromInputValue = (date: string) => new Date(`${date}T00:00:00Z`).toISOString();

const formatRange = (from: string, to: string) => {
  const f = new Date(from);
  const t = new Date(to);
  const fMonth = f.getUTCMonth();
  const tMonth = t.getUTCMonth();
  const fYear = f.getUTCFullYear();
  const tYear = t.getUTCFullYear();
  const fDate = f.getUTCDate();
  const tDate = t.getUTCDate();
  if (fMonth === tMonth && fYear === tYear) {
    return `${fDate}–${tDate} ${MONTHS[fMonth]} ${fYear}`;
  }
  return `${fDate} ${MONTHS[fMonth].slice(0, 4)}. – ${tDate} ${MONTHS[tMonth].slice(0, 4)}. ${tYear}`;
};

const isDefaultDateRange = (from: string) => from.slice(0, 10) === defaultRangeStartISO().slice(0, 10);

interface GlobalFiltersProps {
  /**
   * Permission de l'écran courant. Depuis la v1.12.0 les facettes sont **par
   * permission** : celles du domaine affiché sont exactement les valeurs que sa
   * route acceptera.
   */
  permission: ExtranetPermission;
}

/**
 * Filtres de header : les dimensions de périmètre autorisées pour la permission
 * de l'écran (`GET /v1/users/me` → `filters`), plus la période.
 *
 * Si la permission ne déclare aucune facette, **aucun filtre de périmètre n'est
 * proposé** : l'API filtre déjà au périmètre de l'appelant, et offrir une saisie
 * libre inviterait à taper des valeurs dont on ne sait pas si elles existent.
 */
export const GlobalFilters = ({ permission }: GlobalFiltersProps) => {
  const { scopes, dateRange, setScope, setDateRange, reset } = useFilterStore();
  const { filtersFor } = useAccount();
  const filters = filtersFor(permission);

  // Le périmètre est persisté en sessionStorage. Si le compte ne déclare plus
  // une dimension, ou plus une valeur, le filtre resterait appliqué sans aucun
  // contrôle à l'écran pour le voir ni l'annuler : on purge ce qui n'est plus
  // proposé.
  useEffect(() => {
    const allowed = new Map(filters.map(facet => [facet.key, new Set(facet.values)]));
    for (const [key, values] of Object.entries(scopes)) {
      if (!values?.length) continue;
      const facetValues = allowed.get(key);
      const kept = facetValues ? values.filter(value => facetValues.has(value)) : [];
      if (kept.length !== values.length) setScope(key as ScopeKey, kept);
    }
  }, [filters, scopes, setScope]);

  const hasFilter = countScopeValues(scopes) > 0 || !isDefaultDateRange(dateRange.from);

  return (
    <section aria-label="Filtres globaux" className="flex flex-wrap items-center gap-2">
      {filters.map(facet => (
        <MultiSelect
          key={facet.key}
          label={scopeKeyLabel(facet.key)}
          placeholder="Toutes"
          options={facet.values.map(value => ({ value, label: value, mono: true }))}
          value={scopes[facet.key as ScopeKey] ?? []}
          onChange={values => setScope(facet.key as ScopeKey, values)}
        />
      ))}

      <DateRangeButton from={dateRange.from} to={dateRange.to} onChange={setDateRange} />

      {hasFilter ? (
        <button
          type="button"
          onClick={reset}
          className="ml-auto text-xs text-zinc-500 hover:text-zinc-900 underline-offset-4 hover:underline transition-colors"
        >
          Réinitialiser
        </button>
      ) : null}
    </section>
  );
};

const DateRangeButton = ({
  from,
  to,
  onChange,
}: {
  from: string;
  to: string;
  onChange: (range: { from: string; to: string }) => void;
}) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const esc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('keydown', esc);
    };
  }, [open]);

  const applyPreset = (days: number) => {
    const now = new Date();
    const fromDate = new Date(now.getTime() - (days - 1) * 86_400_000);
    fromDate.setUTCHours(0, 0, 0, 0);
    onChange({ from: fromDate.toISOString(), to: now.toISOString() });
    setOpen(false);
  };

  // La période par défaut. Sans ce raccourci, y revenir passerait par
  // « Réinitialiser », qui efface aussi les filtres de périmètre.
  const applyCurrentWeek = () => {
    onChange({ from: defaultRangeStartISO(), to: new Date().toISOString() });
    setOpen(false);
  };

  const applyMonthOffset = (offset: number) => {
    const now = new Date();
    const f = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + offset, 1, 0, 0, 0, 0));
    const t = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + offset + 1, 0, 23, 59, 59, 999));
    onChange({ from: f.toISOString(), to: t.toISOString() });
    setOpen(false);
  };

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        className="inline-flex items-center gap-2 h-8 px-3 text-sm rounded-md border border-zinc-300 bg-white text-zinc-700 hover:border-zinc-400 hover:bg-zinc-50 transition-colors duration-100"
      >
        <span className="text-zinc-500">Période</span>
        <span className="font-mono tabular-nums text-xs">{formatRange(from, to)}</span>
        <IconChevronDown width={12} height={12} className="text-zinc-400" />
      </button>

      {open ? (
        <div className="absolute top-full mt-1.5 left-0 z-50 w-80 bg-white border border-zinc-200 rounded-md shadow-md p-3 flex flex-col gap-3">
          <div>
            <p className="text-[10px] font-medium uppercase tracking-wide text-zinc-500 mb-1.5">Raccourcis</p>
            <div className="grid grid-cols-2 gap-1.5">
              <PresetButton onClick={applyCurrentWeek}>Semaine en cours</PresetButton>
              <PresetButton onClick={() => applyPreset(7)}>7 derniers jours</PresetButton>
              <PresetButton onClick={() => applyPreset(30)}>30 derniers jours</PresetButton>
              <PresetButton onClick={() => applyMonthOffset(0)}>Mois en cours</PresetButton>
              <PresetButton onClick={() => applyMonthOffset(-1)}>Mois précédent</PresetButton>
              <PresetButton onClick={() => applyPreset(90)}>3 derniers mois</PresetButton>
              <PresetButton onClick={() => applyPreset(365)}>12 derniers mois</PresetButton>
            </div>
          </div>

          <div className="border-t border-zinc-100 pt-3">
            <p className="text-[10px] font-medium uppercase tracking-wide text-zinc-500 mb-2">Période personnalisée</p>
            <div className="grid grid-cols-2 gap-2">
              <DateField
                label="Du"
                value={toInputValue(from)}
                onChange={v => onChange({ from: fromInputValue(v), to })}
                max={toInputValue(to)}
              />
              <DateField
                label="Au"
                value={toInputValue(to)}
                onChange={v => onChange({ from, to: fromInputValue(v) })}
                min={toInputValue(from)}
              />
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

const PresetButton = ({ children, onClick }: { children: React.ReactNode; onClick: () => void }) => (
  <button
    type="button"
    onClick={onClick}
    className="h-8 px-2.5 text-xs text-zinc-700 bg-white border border-zinc-200 rounded hover:bg-zinc-50 hover:border-zinc-300 transition-colors text-left"
  >
    {children}
  </button>
);

const DateField = ({
  label,
  value,
  onChange,
  min,
  max,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  min?: string;
  max?: string;
}) => (
  <label className="flex flex-col gap-1">
    <span className="text-[10px] font-medium uppercase tracking-wide text-zinc-500">{label}</span>
    <input
      type="date"
      value={value}
      min={min}
      max={max}
      onChange={e => onChange(e.target.value)}
      className="h-8 px-2 text-sm border border-zinc-300 rounded text-zinc-900 font-mono tabular-nums focus:outline-none focus:border-zinc-500 focus:ring-2 focus:ring-zinc-900/10"
    />
  </label>
);
